import { Box, Container, Typography, Card, Stack, Divider, Chip, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import { NAVY, NAVY_DARK, GREEN, GREEN_DARK, GREEN_LIGHT, PHONE, cutCornerTR } from '../theme';

export default function TermsAndConditions() {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      {/* Hero Header */}
      <Box sx={{
        pt: { xs: 14, md: 18 },
        pb: { xs: 6, md: 8 },
        bgcolor: NAVY_DARK,
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', top: -100, right: -100, width: 350, height: 350,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(GREEN, 0.2)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              mb: 3,
              '&:hover': { color: GREEN_LIGHT, bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            Back to Home
          </Button>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <Chip
              icon={<GavelIcon sx={{ fontSize: 16, color: `${GREEN_LIGHT} !important` }} />}
              label="Legal Agreement · Investor Terms"
              size="small"
              sx={{ bgcolor: alpha(GREEN, 0.15), color: GREEN_LIGHT, fontWeight: 700 }}
            />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              Last Updated: August 2026
            </Typography>
          </Stack>

          <Typography variant="h1" sx={{
            fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.4rem' },
            fontWeight: 900,
            mb: 2,
            color: '#fff',
          }}>
            Terms and Conditions
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 740, lineHeight: 1.8 }}>
            Please read these Terms and Conditions carefully. By accessing our platform, submitting an investment enquiry,
            or entering into an EV fleet partnership with <strong>VALUEAID INFRASTRUCTURE PRIVATE LIMITED</strong> ("RideXCity"),
            you agree to be bound by the terms detailed herein.
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 }, flex: 1 }}>
        <GridWrapper>
          <Card elevation={0} sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3.5,
            border: `1.5px solid ${alpha(NAVY, 0.1)}`,
            bgcolor: '#ffffff',
          }}>
            <Stack spacing={4}>

              {/* Section 1 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  1. Agreement & Corporate Identity
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  These Terms and Conditions constitute a legally binding agreement between you (the "Investor" or "User") and
                  <strong> VALUEAID INFRASTRUCTURE PRIVATE LIMITED</strong>, an Indian company operating under the brand name
                  <strong> RideXCity</strong>. By investing in our electric fleet units or using our website, you confirm that you have read,
                  understood, and unconditionally accepted all conditions outlined in this document and related Master Service Agreements.
                </Typography>
              </Box>

              <Divider />

              {/* Section 2 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  2. Eligibility & KYC Verification
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                  To participate in RideXCity's EV fleet investment programs:
                </Typography>
                <Stack spacing={1.5} sx={{ pl: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • You must be at least 18 years of age and legally competent to enter into binding contracts under the Indian Contract Act, 1872.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • You must provide valid identity verification documents, including a Permanent Account Number (PAN Card), Aadhaar Card or Passport, and Proof of Active Bank Account.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • Corporate or Institutional investors must provide Certificate of Incorporation, Board Resolution, GSTIN, and authorized signatory KYC.
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              {/* Section 3 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  3. Fleet Structure & Investment Unit Economics
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                  Every EV unit allocated under the investor plan is structured with clear, turnkey component pricing:
                </Typography>

                <Box sx={{
                  p: 2.5,
                  bgcolor: alpha(NAVY, 0.04),
                  borderRadius: 2,
                  border: `1px solid ${alpha(NAVY, 0.08)}`,
                  mb: 2,
                }}>
                  <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 800, mb: 1 }}>
                    Standard Unit Cost Component Breakdown (Total Package Value: ₹99,000):
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                    • <strong>Vehicle Unit Cost:</strong> ₹75,000 (Commercial Grade Electric 2-Wheeler)<br />
                    • <strong>IoT & Telematics Hardware:</strong> ₹5,000 (GPS, 4G Telemetry, Anti-Theft)<br />
                    • <strong>Integration & Onboarding:</strong> ₹8,000 (RideXCity Cloud OS & Platform Setup)<br />
                    • <strong>Battery Pack / Deposit:</strong> ₹5,000 (Swappable Certified Li-Ion Unit)<br />
                    • <strong>Fleet Add-Ons & Safety:</strong> ₹6,000 (Commercial Carrier, Crash Guard, Lock)
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  <strong>Volume Pricing Tiers:</strong><br />
                  • <strong>Plan 1 (1 Unit):</strong> ₹99,000 (₹99,000 / unit)<br />
                  • <strong>Plan 2 (10 Units):</strong> ₹9,00,000 (₹90,000 / unit — saving ₹90,000)<br />
                  • <strong>Plan 3 (25 Units):</strong> ₹21,25,000 (₹85,000 / unit — saving ₹3,50,000)<br />
                  • <strong>Plan 4 (Custom Scale):</strong> Tiered bulk pricing based on total units selected.
                </Typography>
              </Box>

              <Divider />

              {/* Section 4 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  4. Returns, Monthly Payouts & Contract Period
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                  Financial disbursements and contract tenure are managed strictly as follows:
                </Typography>
                <Stack spacing={1.5} sx={{ pl: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Fixed Monthly Return:</strong> Each active EV unit generates a fixed payout of <strong>₹3,000 per month</strong> (amounting to ₹36,000 annually per vehicle unit).
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Contract Period & Payback:</strong> Standard contract tenure is <strong>72 Months (6 Years)</strong> with fixed monthly payouts continuing throughout the contract period without deductions.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Disbursement Schedule:</strong> Payouts are credited directly to the investor's verified bank account between the 1st and 10th of every calendar month.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>TDS & Tax Deductions:</strong> All payouts are subject to Tax Deducted at Source (TDS) under applicable provisions of the Indian Income Tax Act, 1961. TDS certificates (Form 16A) will be issued quarterly.
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              {/* Section 5 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  5. Fleet Operations, Maintenance & IoT Telematics
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  RideXCity assumes full operational responsibility for the deployment, rider allocation, routine maintenance,
                  insurance management, battery swap infrastructure, and commercial leasing of the electric vehicles.
                  Each vehicle is monitored 24/7 via onboard IoT telematics to prevent unauthorized use, ensure safety compliance,
                  and guarantee operational uptime.
                </Typography>
              </Box>

              <Divider />

              {/* Section 6 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  6. Asset Security, Insurance & Risk Mitigation
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  All electric two-wheelers in the fleet are protected by comprehensive commercial vehicle insurance, manufacturer battery warranties,
                  and anti-theft GPS immobilizers. In the rare event of total loss or irreversible damage, insurance recovery procedures
                  will be executed by RideXCity in accordance with the Master Investor Agreement.
                </Typography>
              </Box>

              <Divider />

              {/* Section 7 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  7. Governing Law & Dispute Resolution
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  These Terms and Conditions shall be governed by and construed in accordance with the laws of the Republic of India.
                  Any dispute, controversy, or claim arising out of or relating to these terms shall be subject to the exclusive jurisdiction
                  of the competent courts in India.
                </Typography>
              </Box>

              <Divider />

              {/* Section 8 - Contact Information */}
              <Box sx={{
                ...cutCornerTR(16),
                p: 3.5,
                bgcolor: alpha(GREEN, 0.06),
                border: `1.5px solid ${alpha(GREEN, 0.3)}`,
              }}>
                <Typography variant="h6" sx={{ color: NAVY, fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VerifiedUserIcon sx={{ color: GREEN_DARK }} /> 8. Legal Notices & Investor Desk
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                  For official inquiries, contract agreements, or clarifications regarding these terms, please contact:
                </Typography>
                <Stack spacing={1.2}>
                  <Typography variant="body2" sx={{ color: NAVY, fontWeight: 700 }}>
                    VALUEAID INFRASTRUCTURE PRIVATE LIMITED (Brand: RideXCity)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon sx={{ fontSize: 18, color: GREEN_DARK }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Legal Desk: <strong>legal@ridexcity.com</strong> / <strong>investor@ridexcity.com</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon sx={{ fontSize: 18, color: GREEN_DARK }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Helpline: <strong>{PHONE}</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOnIcon sx={{ fontSize: 18, color: GREEN_DARK, mt: 0.3 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Corporate Headquarters: India &nbsp;|&nbsp; <strong>www.ridexcity.com</strong>
                    </Typography>
                  </Box>
                </Stack>
              </Box>

            </Stack>
          </Card>
        </GridWrapper>
      </Container>

      <Footer />
      <FloatingActions />
    </Box>
  );
}

function GridWrapper({ children }) {
  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      {children}
    </Box>
  );
}
